import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, FileCheck2, FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Reusable lot-trace dialog.
// `sections` controls which linkage rows to display: any subset of ["coas", "compounding_logs"].
export const TraceDialog = ({ open, onClose, lot, sections = ["coas", "compounding_logs"] }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !lot) return;
    setLoading(true); setData(null);
    api.get(`/traceability/lot/${encodeURIComponent(lot)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Trace failed"))
      .finally(() => setLoading(false));
  }, [open, lot]);

  const show = (k) => sections.includes(k);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="trace-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Package className="w-5 h-5 text-pink-700" /> Lot Traceability — <span className="font-mono">{lot}</span>
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-pink-700" /></div>
        ) : !data ? null : (
          <div className="space-y-5">
            {show("coas") && (
              <section>
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                  <FileCheck2 className="w-4 h-4 text-pink-700" /> Certificates of Analysis ({data.coas.length})
                </h3>
                {data.coas.length === 0 ? (
                  <p className="text-sm text-slate-400">No COA on file for this lot.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="trace-coa-list">
                    {data.coas.map((c) => (
                      <li key={c.id} className="text-sm p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                        <div className="font-medium text-slate-800">
                          {c.product_name} <span className="text-slate-400 font-normal">— {c.supplier}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">
                          Received {c.date_received} · Expires {c.expiration_date || "—"} · Grade {c.grade || "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            {show("compounding_logs") && (
              <section>
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                  <FlaskConical className="w-4 h-4 text-pink-700" /> Compounding logs using this lot ({data.compounding_logs.length})
                </h3>
                {data.compounding_logs.length === 0 ? (
                  <p className="text-sm text-slate-400">No compounding logs reference this lot yet.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="trace-log-list">
                    {data.compounding_logs.map((l) => (
                      <li key={l.id} className="text-sm p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                        <div className="font-medium text-slate-800">
                          {l.compound_name || "—"}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">
                          {l.log_date} · by {l.performed_by || "—"} · Result: {l.result || "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
